import { pipe, Option, Array as A } from 'effect';
import moment from 'moment';
import { extractEmail } from 'lib/helpers';

import type { WorkspaceSearchQuery } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/querySchema';

export type QueryChipType = 'text' | 'date' | 'invoice_date' | 'payment_date' | 'label';

export interface QueryChip {
  readonly type: QueryChipType;
  readonly value: string;
}

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const DATE_TYPES = ['date', 'invoice_date', 'payment_date'] as const;

// A date filter is stored as a [from, to] pair; render it as a single "from - to"
// chip — faithful to the legacy FavoriteTagsPanel `dateParser` (format each end
// with the company date format, join with " - ").
const dateChip = (
  query: WorkspaceSearchQuery,
  type: (typeof DATE_TYPES)[number],
  dateFormat: string,
): Option.Option<QueryChip> =>
  pipe(
    Option.fromNullable(query[type]),
    Option.filter((values) => values.length > 0),
    Option.map((values) => ({
      type,
      value: values.map((v) => moment(v).format(dateFormat || DEFAULT_DATE_FORMAT)).join(' - '),
    })),
  );

// `label` is a system user attribute carried by saved Favorites; its display value
// prefers an email parsed out of the raw `query` expression, else the label itself.
const labelChip = (query: WorkspaceSearchQuery): Option.Option<QueryChip> =>
  pipe(
    Option.fromNullable(query.label),
    Option.map((label) => {
      const email = pipe(
        Option.fromNullable(query.query),
        Option.flatMap((raw) => Option.fromNullable(extractEmail(raw.split(' ').join('+')))),
        Option.getOrNull,
      );
      return { type: 'label' as const, value: email ?? label };
    }),
  );

// Query-derived chips, in a stable [text, date-types, label] order. Mirrors the
// legacy `currentTags` derivation but pure and origin-tagged so each chip's
// removal can be routed back to its own URL-query key.
export const deriveQueryChips = (query: WorkspaceSearchQuery, dateFormat = DEFAULT_DATE_FORMAT): QueryChip[] => {
  const textChips = pipe(
    Option.fromNullable(query.text),
    Option.getOrElse((): string[] => []),
    A.map((value): QueryChip => ({ type: 'text', value })),
  );
  const dateChips = pipe(
    DATE_TYPES,
    A.filterMap((type) => dateChip(query, type, dateFormat)),
  );
  const label = pipe(
    labelChip(query),
    Option.match({ onNone: (): QueryChip[] => [], onSome: (chip) => [chip] }),
  );
  return [...textChips, ...dateChips, ...label];
};

export interface OverflowSplit<T> {
  readonly visible: T[];
  readonly hidden: T[];
}

// Fixed-count overflow: the first `maxVisible` chips stay inline, the rest collapse
// behind the "+N" control. Threshold value is non-binding (Q1).
// FUTURE (Q1): replace the fixed cutoff with a width-measurement pass
// (ResizeObserver on the chip strip) so the visible count adapts to the actual
// input width (1440 vs 1900) instead of a constant.
// NB: a generic `function` declaration, not a `<T>(…) =>` arrow — the esbuild-loader
// parses every file (incl. .ts) in `tsx` mode, where a bare generic arrow reads as a
// JSX tag. Matches the repo idiom (e.g. lib/safeJsonParse.ts).
export function splitOverflow<T>(chips: readonly T[], maxVisible: number): OverflowSplit<T> {
  return pipe(chips, A.splitAt(maxVisible), ([visible, hidden]) => ({ visible, hidden }));
}
